import { LineString } from "ol/geom";

import { EnvState } from "../envConfig";

import {
  MODE_PARAM,
  NORTH_ARROW_PARAM,
  QR_CODE_PARAM,
  SCALEBAR_PARAM,
} from "./constants";
import { getReadUrl } from "./getShortenedUrl";

import type { Extent } from "ol/extent";

/**
 * Build the "read URL" (the print-mode page to screenshot) plus the export's
 * width/height/deviceScaleFactor, sent to the backend for PDF export. The
 * backend builds the actual screenshot-service request URL server-side from
 * these values (never trusting a client-supplied host) - this function only
 * computes what the export itself needs, same as before.
 */
const getLayoutScreenshotUrl = (
  state: EnvState,
  readUrl?: string,
): {
  deviceScaleFactor: number;
  height: number;
  readUrl: string;
  width: number;
} => {
  const {
    exportCoordinates,
    exportLayout,
    exportSize,
    kmlAdminId,
    kmlReadId,
    mapsetEditorUrl,
    previewPathname,
  } = state.app;
  const { olMap } = state.map;

  const { mapHeight, mapWidth, qrCode } = exportLayout.properties;
  const size = exportSize || [mapWidth, mapHeight];
  let extent: Extent;
  try {
    extent = exportCoordinates
      ? new LineString(exportCoordinates).getExtent()
      : olMap?.getView()?.calculateExtent(size);
  } catch {
    // eslint-disable-next-line no-console
    console.log("No extent defined");
  }

  let newReadUrl = readUrl;

  if (!newReadUrl) {
    newReadUrl = getReadUrl(
      // To facilitate the dev in frontend without installing the server. The screenshot service has no access to localhost.
      `${window.location.href.replace(
        /http:\/\/localhost:\d+/,
        mapsetEditorUrl,
      )}&${MODE_PARAM}=print&${NORTH_ARROW_PARAM}=true&${SCALEBAR_PARAM}=true&legallinks=false${qrCode ? `&${QR_CODE_PARAM}=true` : ""}`,
      kmlAdminId,
      kmlReadId,
      previewPathname,
      extent,
    );
  }

  const width = size[0];
  const height = size[1];
  let deviceScaleFactor = 1;

  // If the width is not enough big to fit properly the template we increase the deviceScaleFactor
  if (mapWidth > 0) {
    while (
      width * deviceScaleFactor < mapWidth * 2.2 &&
      deviceScaleFactor < 5
    ) {
      deviceScaleFactor += 1;
    }
  }

  return {
    deviceScaleFactor,
    height: parseInt(`${height}`, 10),
    readUrl: newReadUrl,
    width: parseInt(`${width}`, 10), // Screenshot service manages only integer
  };
};

export default getLayoutScreenshotUrl;
